import React, { useMemo } from 'react'
import CarHirePageHeader from './CarHirePageHeader'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { useGlobalCarHireSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalCarHireSearch'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import styled from 'styled-components'
import Pane from 'components/Common/Pane'
import Heading from 'LuxKit/Typography/Heading'
import LayoutContainer from 'components/Common/LayoutContainer'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import CarHireVerticalUSPs from './CarHireVerticalUSPs'
import { HARDCODED_SUPPLIERS, getCarHireFAQ } from '../CarHireHardcodedLists'
import Image from 'components/Common/Image/Image'
import Accordion from 'LuxKit/Accordion'
import MarkdownRender from 'LuxKit/MarkdownRender'
import CarHirePageUpcomingOrderCarousels from './CarHirePageUpcomingOrderCarousels'
import { connect } from 'react-redux'
import { isCarHireInsuranceAvailable, isBundledCarHireEnabled } from 'selectors/carHireSelectors'
import CarHireBundledUpcomingOrders from '../CarHireBundledUpcomingOrders/CarHireBundledUpcomingOrders'
import useOrderCarHire from 'hooks/CarHire/useOrderCarHire'
import { useAppSelector } from 'hooks/reduxHooks'
import { useIntl } from 'react-intl'

const SuppliersSection = styled(Pane)`
  padding: ${rem(40)} 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(64)} 0;
  }
`

const USPsSection = styled(Pane)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(46)};
    padding-bottom: ${rem(46)};
  }
`

const FAQSection = styled(Pane)`
  padding: ${rem(40)} 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(60)}
  }
`

const SupplierLogoWrapper = styled.div`
  padding: ${rem(8)} ${rem(16)};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  display: flex;
  align-items: center;
  justify-content: center;
`

const SuppliersLogoCarousel = styled(CardCarousel)`
  margin-top: ${rem(24)};
`

const AccordionWrapper = styled.div`
  margin-top: ${rem(48)};
`

const UpcomingOrdersCarouselSection = styled(Pane)`
  padding: ${rem(40)} 0;
`

interface MappedProps {
  isCarHireInsuranceAvailable: boolean
  isBundledCarHireEnabled: boolean
}

interface Props extends MappedProps {
}

function CarHirePage(props: Props) {
  const { isCarHireInsuranceAvailable, isBundledCarHireEnabled } = props
  const intl = useIntl()
  const { globalCarHireSearchDispatch, globalCarHireSearchState } = useGlobalCarHireSearchContext()
  const { hotelOrders, isHotelOrderFetched } = useOrderCarHire()

  const ordersIds = useMemo(() => {
    if (!hotelOrders.length) return []
    return hotelOrders.map((order) => order.id)
  }, [hotelOrders])

  const hasBundleEligibility = useAppSelector(state => {
    if (!ordersIds.length) return false
    return (
      ordersIds.every((id) => !state.carHire.bundleEligibilityOrdersList[id]) ||
      ordersIds.some((id) => state.carHire.bundleEligibilityOrdersList[id]?.isEligible)
    )
  })

  const isBundledOrderEligibilityFetching = useAppSelector(state => {
    if (!ordersIds.length) return false
    return (
      ordersIds.every((id) => !state.carHire.bundleEligibilityOrdersList[id]) ||
      ordersIds.some((id) => state.carHire.bundleEligibilityOrdersList[id]?.fetching)
    )
  })

  // Checks if there are any vehicles available for the orders eligible for bundled car hire
  const hasBundledOrderVehicles = useAppSelector(state => {
    const listsKeys = Object.keys(state.carHire.offerLists)
    const ordersIds = Object.keys(state.carHire.bundleEligibilityOrdersList)
    return ordersIds.some((id) => {
      const listKey = listsKeys.find((key) => key.includes(id))
      if (!listKey) return true
      return state.carHire.offerLists[listKey].fetching || state.carHire.offerLists[listKey].vehicles.length > 0
    })
  })

  const sortedOrdersIds = useAppSelector(state => {
    if (!ordersIds.length) return
    return [...ordersIds].sort((idA, idB) => {
      const startDateA = state.carHire.bundleEligibilityOrdersList[idA]?.earliestItem?.checkIn ?? new Date()
      const startDateB = state.carHire.bundleEligibilityOrdersList[idB]?.earliestItem?.checkIn ?? new Date()
      return new Date(startDateA).getTime() - new Date(startDateB).getTime()
    }).join(',')
  })

  const sortedOrders = useMemo(() => {
    const ids = sortedOrdersIds?.split(',') ?? []
    return ids.map(id => hotelOrders.find(order => order.id === id)).filter(Boolean) as Array<App.Order>
  }, [sortedOrdersIds, hotelOrders])

  const showBundledCarHire = isBundledCarHireEnabled && hasBundleEligibility && (hasBundledOrderVehicles || isBundledOrderEligibilityFetching)
  const isFetchingUpcomingOrders = !isHotelOrderFetched

  return (
    <div>
      <GlobalSearchContextProvider state={globalCarHireSearchState} dispatch={globalCarHireSearchDispatch}>
        <div id="carHirePageHeader">
          <CarHirePageHeader />
        </div>
        {!isFetchingUpcomingOrders && !showBundledCarHire && <CarHirePageUpcomingOrderCarousels />}
        {!isFetchingUpcomingOrders && showBundledCarHire && <UpcomingOrdersCarouselSection>
          <CarHireBundledUpcomingOrders
            orders={sortedOrders}
            title={intl.formatMessage({
              id: '5CYqG4CBoS',
              defaultMessage: 'Book a car for your next trip before the prices go up',
              description: 'Heading for the bundled car hire section promoting booking a car for upcoming trips',
            })}
          />
        </UpcomingOrdersCarouselSection>}
        <SuppliersSection>
          <LayoutContainer>
            <Heading variant="heading3">
              {intl.formatMessage(
                {
                  id: 'lD6muIoUce',
                  defaultMessage: 'Featured <italic>suppliers</italic>',
                  description: 'Section heading for featured suppliers section in the car hire section',
                },
                {
                  italic: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
                },
              )}
            </Heading>
            <SuppliersLogoCarousel pageSize={8} gap={12}>
              {HARDCODED_SUPPLIERS.map((supplier) => (
                <SupplierLogoWrapper key={supplier.name}>
                  <Image width={80} image={supplier.image} alt={supplier.name} />
                </SupplierLogoWrapper>
              ))}
            </SuppliersLogoCarousel>
          </LayoutContainer>
        </SuppliersSection>
        <USPsSection background="neutral-seven">
          <LayoutContainer>
            <Heading variant="heading3">
              {intl.formatMessage({
                id: 'ZXEqUXuvsq',
                defaultMessage: 'Why you should book with us',
                description: 'Section heading for why book with us section in the car hire section',
              })}
            </Heading>
            <CarHireVerticalUSPs />
          </LayoutContainer>
        </USPsSection>
        <FAQSection>
          <LayoutContainer variant="medium">
            <Heading variant="heading3" align="center">
              {intl.formatMessage({
                id: 'TAfz8krzDH',
                defaultMessage: 'Frequently asked questions',
                description: 'Section heading for the frequently asked questions area on the offer page',
              })}
            </Heading>
            <AccordionWrapper>
              {getCarHireFAQ(isCarHireInsuranceAvailable, intl).map((item) => (
                <Accordion
                  key={item.question}
                  startIcon={item.icon}
                  heading={item.question}
                  size="large"
                >
                  <MarkdownRender
                    fontSize="medium"
                    content={item.answer}
                    type="compact"
                  />
                </Accordion>
              ))}
            </AccordionWrapper>
          </LayoutContainer>
        </FAQSection>
      </GlobalSearchContextProvider>
    </div>
  )
}

function mapStateToProps(state: App.State) {
  return {
    isCarHireInsuranceAvailable: isCarHireInsuranceAvailable(state),
    isBundledCarHireEnabled: isBundledCarHireEnabled(state),
  }
}

export default connect(mapStateToProps)(CarHirePage)
